import React, { FC } from 'react';
import { AdaptiveTabs } from './AdaptiveTabs.styles';
import { BhiDrawer } from 'src/UIKit';
import { AwayMessageController } from '../../controllers';
import { Box, Tab, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { CustomMessageForm } from './CustomMessageForm';
import { useSelector } from 'react-redux';
import { getAwayCustomMessageCampaign, getAwayRecurringMessagesCampaign } from '../../slices';
import { RecurringMessagesForm } from './RecurringMessagesForm';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';

interface AwayMessageDrawerProps extends AwayMessageController {}

export const AwayMessageDrawer: FC<AwayMessageDrawerProps> = ({
  isOpen,
  close,
  customFields,
  messageTemplates,
  companyAvailabilityStatus,
  tab,
  handleTabChange,
  submitSaveCustomAwayMessage,
  submitSaveRecurringAwayMessages,
  isCustomFormDirty,
  setIsCustomFormDirty,
  isRecurringFormDirty,
  setIsRecurringFormDirty,
  shouldActivateCampaign,
}) => {
  const awayCustomMessageCampaign = useSelector(getAwayCustomMessageCampaign);
  const awayRecurringMessagesCampaign = useSelector(getAwayRecurringMessagesCampaign);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const hasUnsavedChanges = isCustomFormDirty || isRecurringFormDirty;

  const resetFormDirtyFlags = () => {
    setIsCustomFormDirty(false);
    setIsRecurringFormDirty(false);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      openDialog({
        title: 'You have unsaved changes!',
        content: 'Do you want to continue without saving?',
        onConfirm: () => {
          resetFormDirtyFlags();
          close();
        },
      });
    } else {
      resetFormDirtyFlags();
      close();
    }
  };

  const handleTabChangeWithConfirmation = (
    _: React.SyntheticEvent,
    newTab: 'custom' | 'recurring',
  ) => {
    const isSwitchingToCustom = newTab === 'custom';
    const isSwitchingToRecurring = newTab === 'recurring';

    const isOtherFormDirty =
      (isSwitchingToCustom && isRecurringFormDirty) ||
      (isSwitchingToRecurring && isCustomFormDirty);

    if (isOtherFormDirty) {
      openDialog({
        title: 'You have unsaved changes!',
        content: 'Do you want to continue without saving?',
        onConfirm: () => {
          if (isSwitchingToCustom) setIsRecurringFormDirty(false);
          if (isSwitchingToRecurring) setIsCustomFormDirty(false);
          handleTabChange(_, newTab);
        },
      });
    } else {
      resetFormDirtyFlags();
      handleTabChange(_, newTab);
    }
  };

  const companyStatus = companyAvailabilityStatus === 'away' ? 'away' : 'available';

  if (!awayCustomMessageCampaign || !awayRecurringMessagesCampaign) {
    return null;
  }

  const immediateMessage = awayCustomMessageCampaign.scheduledMessages.find(
    m => m.scheduledType === 'immediate',
  );

  return (
    <BhiDrawer isOpen={isOpen} onClose={handleClose} title="Away Message">
      <ConfirmationDialog />
      <Box sx={{ 
        marginTop: isMobile ? '-20px' : '-30px',
        padding: isMobile ? '0 8px' : '0'
      }}>
        <AdaptiveTabs
          value={tab}
          onChange={handleTabChangeWithConfirmation}
          compact={isMobile}
          sx={{ marginBottom: '20px' }}
        >
          <Tab label="Custom" value="custom" key="custom" />
          <Tab label="Recurring" value="recurring" key="recurring" />
        </AdaptiveTabs>

        {tab === 'custom' && (
          <CustomMessageForm
            awayMessageCampaign={awayCustomMessageCampaign}
            customFields={customFields}
            messageTemplates={messageTemplates}
            companyStatus={companyStatus}
            message={immediateMessage}
            setIsCustomFormDirty={setIsCustomFormDirty}
            submitSaveCustomAwayMessage={submitSaveCustomAwayMessage}
            shouldActivateCampaign={shouldActivateCampaign}
          />
        )}

        {tab === 'recurring' && (
          <RecurringMessagesForm
            awayMessageCampaign={awayRecurringMessagesCampaign}
            customFields={customFields}
            messageTemplates={messageTemplates}
            companyStatus={companyStatus}
            setIsRecurringFormDirty={setIsRecurringFormDirty}
            submitSaveRecurringAwayMessages={submitSaveRecurringAwayMessages}
            shouldActivateCampaign={shouldActivateCampaign}
          />
        )}
      </Box>
    </BhiDrawer>
  );
};
